import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View, Text, FlatList, Image, StyleSheet, ActivityIndicator,
  Modal, TextInput, Switch, ScrollView, TouchableOpacity, Alert,
  useWindowDimensions,
} from 'react-native';
import {
  fetchProducts, createProduct, updateProduct, deleteProduct,
  uploadEcomImage, deleteStorageUrl,
} from '../query/productService';
import { ProductModel, ColorInSize } from '../model/productModel';

// ── Colour palette ────────────────────────────────────────────────
const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  success: '#10b981', successLight: '#d1fae5',
  danger: '#ef4444', dangerLight: '#fee2e2',
  warn: '#f59e0b', warnLight: '#fef3c7',
  bg: '#f6f8fb', card: '#fff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

const selectStyle: React.CSSProperties = {
  width: '100%', height: 46, backgroundColor: '#fafafa',
  border: '1px solid #e2e8f0', borderRadius: 10,
  paddingLeft: 14, paddingRight: 14, fontSize: 14,
  color: '#1a2332', cursor: 'pointer', outline: 'none',
};

// ── Empty model ──────────────────────────────────────────────────
const emptyModel = (): Omit<ProductModel, 'id'> => ({
  productName: '', primaryImage: '', returnPolicy: '',
  warranty: '', category: '', subcategory: '', nestedsubcategory: '',
  discounted: false, aproved: false, sponsored: false,
  stockAvailability: true, images: [], features: [], searchKey: [],
  colors: [], reviews: [],
  branch: { branchId: '', branchLocation: '', branchName: '' },
});

// ── Main Component ───────────────────────────────────────────────
export default function EcomProductList() {
  const { width } = useWindowDimensions();
  const isMobile = width < 600;
  const cols = width < 600 ? 1 : width < 900 ? 2 : width < 1200 ? 3 : 5;
  const primaryImgRef = useRef<HTMLInputElement>(null);
  const galleryImgRef = useRef<HTMLInputElement>(null);

  const [products, setProducts] = useState<ProductModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState<string | null>(null); // 'primary' | 'gallery' | null

  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<ProductModel, 'id'>>(emptyModel());

  // Colour/size editor within modal
  const [newColorName, setNewColorName] = useState('');
  const [newColorHex, setNewColorHex] = useState('#000000');

  // Filters
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all'|'approved'|'pending'|'sale'|'sponsored'>('all');

  // ── Load ──────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try { setProducts(await fetchProducts()); }
    catch { Alert.alert('Error', 'Could not load products'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Unique vendor list ─────────────────────────────────────────
  const vendors = Array.from(
    new Map(
      products
        .filter(p => p.branch?.branchId)
        .map(p => [p.branch!.branchId, p.branch!.branchName || p.branch!.branchId])
    ).entries()
  );

  // ── Unique category list (from loaded products) ────────────────
  const categories = Array.from(
    new Set(products.map(p => p.category).filter(Boolean) as string[])
  ).sort();

  // ── Filtered list ──────────────────────────────────────────────
  const filtered = products.filter(p => {
    const nameMatch = !search || (p.productName ?? '').toLowerCase().includes(search.toLowerCase());
    const vendorMatch = !vendorFilter || p.branch?.branchId === vendorFilter;
    const categoryMatch = !categoryFilter || p.category === categoryFilter;
    const statusMatch =
      statusFilter === 'all' ? true :
      statusFilter === 'approved' ? !!p.aproved :
      statusFilter === 'pending' ? !p.aproved :
      statusFilter === 'sale' ? !!p.discounted :
      statusFilter === 'sponsored' ? !!p.sponsored : true;
    return nameMatch && vendorMatch && categoryMatch && statusMatch;
  });

  // ── Form helpers ──────────────────────────────────────────────
  const setF = (key: keyof Omit<ProductModel, 'id'>, val: any) =>
    setForm(prev => ({ ...prev, [key]: val }));

  const openCreate = () => {
    setForm(emptyModel());
    setEditingId(null);
    setNewColorName(''); setNewColorHex('#000000');
    setModalVisible(true);
  };

  const openEdit = (p: ProductModel) => {
    const { id, ...rest } = p;
    setForm({ ...emptyModel(), ...rest });
    setEditingId(id ?? null);
    setNewColorName(''); setNewColorHex('#000000');
    setModalVisible(true);
  };

  // ── Save ──────────────────────────────────────────────────────
  const onSave = async () => {
    if (!form.productName?.trim()) { Alert.alert('Validation', 'Product name is required'); return; }
    try {
      if (editingId) {
        await updateProduct(editingId, form);
      } else {
        await createProduct(form);
      }
      setModalVisible(false);
      load();
      Alert.alert('Success', `Product ${editingId ? 'updated' : 'created'}`);
    } catch { Alert.alert('Error', 'Failed to save product'); }
  };

  // ── Delete ────────────────────────────────────────────────────
  const onDelete = async (p: ProductModel) => {
    // Alert.alert with buttons is a no-op on react-native-web, so use the
    // browser confirm dialog for an actual working delete on the web admin.
    const ok = typeof window !== 'undefined'
      ? window.confirm(`Delete "${p.productName}"? This cannot be undone.`)
      : false;
    if (!ok) return;
    try {
      await deleteProduct(p.id!, p.primaryImage, p.images);
      load();
    } catch {
      window.alert('Failed to delete product');
    }
  };

  // ── Quick toggles from card ───────────────────────────────────
  const toggleField = async (p: ProductModel, field: 'aproved' | 'discounted' | 'sponsored') => {
    try {
      await updateProduct(p.id!, { [field]: !p[field] });
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, [field]: !x[field] } : x));
    } catch { Alert.alert('Error', 'Update failed'); }
  };

  // ── Image upload helpers ──────────────────────────────────────
  const handlePrimaryImg = async (e: any) => {
    const file: File = e.target.files?.[0];
    if (!file) return;
    setImgUploading('primary');
    try {
      const id = editingId || `new_${Date.now()}`;
      const url = await uploadEcomImage(file, id, 'primary');
      setF('primaryImage', url);
    } catch { Alert.alert('Error', 'Upload failed'); }
    setImgUploading(null);
    e.target.value = '';
  };

  const handleGalleryImg = async (e: any) => {
    const files: FileList = e.target.files;
    if (!files?.length) return;
    setImgUploading('gallery');
    try {
      const id = editingId || `new_${Date.now()}`;
      const urls: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const url = await uploadEcomImage(files[i], id, `gallery_${Date.now()}_${i}`);
        urls.push(url);
      }
      setF('images', [...(form.images ?? []), ...urls]);
    } catch { Alert.alert('Error', 'Gallery upload failed'); }
    setImgUploading(null);
    e.target.value = '';
  };

  const removeGalleryImg = async (url: string) => {
    setF('images', (form.images ?? []).filter((u: string) => u !== url));
    await deleteStorageUrl(url);
  };

  // ── Colour/size management ────────────────────────────────────
  const addColor = () => {
    if (!newColorName.trim()) return;
    const colors = [...(form.colors ?? []), { colorName: newColorName.trim(), sizes: [] }];
    setF('colors', colors);
    setNewColorName(''); setNewColorHex('#000000');
  };

  const removeColor = (idx: number) =>
    setF('colors', (form.colors ?? []).filter((_: any, i: number) => i !== idx));

  const addSize = (colorIdx: number, size: ColorInSize) => {
    const colors = (form.colors ?? []).map((c: any, i: number) =>
      i === colorIdx ? { ...c, sizes: [...(c.sizes ?? []), size] } : c
    );
    setF('colors', colors);
  };

  const removeSize = (colorIdx: number, sizeIdx: number) => {
    const colors = (form.colors ?? []).map((c: any, i: number) =>
      i === colorIdx ? { ...c, sizes: (c.sizes ?? []).filter((_: any, j: number) => j !== sizeIdx) } : c
    );
    setF('colors', colors);
  };

  // ── Render card ───────────────────────────────────────────────
  const renderCard = ({ item }: { item: ProductModel }) => (
    <View style={[card.wrap, isMobile && { width: '100%' }]}>
      {/* Image */}
      <View style={card.imgWrap}>
        {item.primaryImage
          ? <Image source={{ uri: item.primaryImage }} style={card.img} resizeMode="cover" />
          : <View style={[card.img, card.imgPlaceholder]}><Text style={{ fontSize: 40 }}>🛍️</Text></View>
        }
        {/* Status badges */}
        <View style={card.badges}>
          <View style={[card.badge, { backgroundColor: item.aproved ? C.success : C.warn }]}>
            <Text style={card.badgeText}>{item.aproved ? '✓ Approved' : '⏱ Pending'}</Text>
          </View>
          {item.discounted && (
            <View style={[card.badge, { backgroundColor: C.danger }]}>
              <Text style={card.badgeText}>SALE</Text>
            </View>
          )}
          {item.sponsored && (
            <View style={[card.badge, { backgroundColor: '#7c3aed' }]}>
              <Text style={card.badgeText}>Sponsored</Text>
            </View>
          )}
        </View>
      </View>

      {/* Body */}
      <View style={card.body}>
        <Text style={card.name} numberOfLines={2}>{item.productName || 'Untitled'}</Text>

        {/* Vendor */}
        {item.branch?.branchName ? (
          <View style={card.vendorRow}>
            <Text style={card.vendorIcon}>🏪</Text>
            <Text style={card.vendorText} numberOfLines={1}>{item.branch.branchName}</Text>
          </View>
        ) : null}

        {/* Category chips */}
        <View style={card.chips}>
          {item.category ? <View style={card.chip}><Text style={card.chipText}>{item.category}</Text></View> : null}
          {item.subcategory ? <View style={[card.chip, { backgroundColor: '#e0f2fe' }]}><Text style={[card.chipText, { color: '#0369a1' }]}>{item.subcategory}</Text></View> : null}
        </View>

        {/* Stats row */}
        <View style={card.statsRow}>
          <View style={card.stat}>
            <Text style={card.statLabel}>Colors</Text>
            <Text style={card.statValue}>{item.colors?.length ?? 0}</Text>
          </View>
          <View style={card.stat}>
            <Text style={card.statLabel}>Gallery</Text>
            <Text style={card.statValue}>{item.images?.length ?? 0}</Text>
          </View>
          <View style={card.stat}>
            <Text style={card.statLabel}>Features</Text>
            <Text style={card.statValue}>{item.features?.length ?? 0}</Text>
          </View>
        </View>

        {/* Quick toggles */}
        <View style={card.toggleRow}>
          <TouchableOpacity
            style={[card.toggleBtn, item.aproved && { backgroundColor: C.successLight }]}
            onPress={() => toggleField(item, 'aproved')}
          >
            <Text style={[card.toggleText, { color: item.aproved ? C.success : C.textMuted }]}>
              {item.aproved ? '✓ Approved' : 'Approve'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[card.toggleBtn, item.discounted && { backgroundColor: '#fee2e2' }]}
            onPress={() => toggleField(item, 'discounted')}
          >
            <Text style={[card.toggleText, { color: item.discounted ? C.danger : C.textMuted }]}>
              {item.discounted ? '🏷 On Sale' : 'Mark Sale'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Gallery strip */}
      {(item.images ?? []).length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={card.gallery}>
          {item.images!.map((uri, i) => (
            <Image key={i} source={{ uri }} style={card.galleryImg} resizeMode="cover" />
          ))}
        </ScrollView>
      )}

      {/* Actions */}
      <View style={card.actions}>
        <TouchableOpacity style={card.editBtn} onPress={() => openEdit(item)}>
          <Text style={card.editText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity style={card.delBtn} onPress={() => onDelete(item)}>
          <Text style={card.delText}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // ── Render ─────────────────────────────────────────────────────
  return (
    <View style={s.container}>
      {/* Header */}
      <View style={[s.header, isMobile && s.headerMobile]}>
        <View>
          <Text style={s.title}>Ecom Store</Text>
          <Text style={s.subtitle}>{filtered.length} products</Text>
        </View>
        <TouchableOpacity style={s.addBtn} onPress={openCreate}>
          <Text style={s.addBtnText}>+ Add Product</Text>
        </TouchableOpacity>
      </View>

      {/* Filters */}
      <View style={s.filtersRow}>
        <View style={[s.searchBox, { flex: 1 }]}>
          <Text style={s.searchIcon}>🔍</Text>
          <TextInput
            style={s.searchInput}
            placeholder="Search products..."
            value={search}
            onChangeText={setSearch}
            placeholderTextColor={C.textMuted}
          />
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Text style={{ color: C.textMuted, padding: 4 }}>✕</Text></TouchableOpacity> : null}
        </View>
        <View style={{ minWidth: 160 }}>
          <select
            value={vendorFilter}
            onChange={(e: any) => setVendorFilter(e.target.value)}
            style={{ ...selectStyle, height: 46 }}
          >
            <option value="">All Vendors</option>
            {vendors.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </View>
        <View style={{ minWidth: 160 }}>
          <select
            value={categoryFilter}
            onChange={(e: any) => setCategoryFilter(e.target.value)}
            style={{ ...selectStyle, height: 46 }}
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </View>
        <View style={{ minWidth: 150 }}>
          <select
            value={statusFilter}
            onChange={(e: any) => setStatusFilter(e.target.value)}
            style={{ ...selectStyle, height: 46 }}
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="sale">On Sale</option>
            <option value="sponsored">Sponsored</option>
          </select>
        </View>
      </View>

      {loading ? (
        <View style={s.loader}>
          <ActivityIndicator size="large" color={C.primary} />
          <Text style={s.loaderText}>Loading products...</Text>
        </View>
      ) : filtered.length === 0 ? (
        <View style={s.empty}>
          <Text style={s.emptyIcon}>🛍️</Text>
          <Text style={s.emptyTitle}>No products found</Text>
          <Text style={s.emptySubtitle}>Add your first ecom product</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={item => item.id ?? item.productId ?? String(Math.random())}
          renderItem={renderCard}
          numColumns={cols}
          key={String(cols)}
          columnWrapperStyle={cols > 1 ? { gap: 10 } : undefined}
          contentContainerStyle={{ paddingBottom: 60, gap: 10 }}
        />
      )}

      {/* ── Edit / Create Modal ────────────────────────────────── */}
      <Modal visible={modalVisible} animationType="slide" onRequestClose={() => setModalVisible(false)}>
        <View style={ms.screen}>
          {/* Modal header */}
          <View style={ms.header}>
            <TouchableOpacity onPress={() => setModalVisible(false)} style={ms.backBtn}>
              <Text style={ms.backText}>✕ Cancel</Text>
            </TouchableOpacity>
            <Text style={ms.headerTitle}>{editingId ? 'Edit Product' : 'New Product'}</Text>
            <TouchableOpacity style={ms.saveBtn} onPress={onSave}>
              <Text style={ms.saveBtnText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView contentContainerStyle={ms.scroll} keyboardShouldPersistTaps="handled">
            {/* Hidden file inputs */}
            <input type="file" accept="image/*" ref={primaryImgRef as any} style={{ display: 'none' }} onChange={handlePrimaryImg} />
            <input type="file" accept="image/*" multiple ref={galleryImgRef as any} style={{ display: 'none' }} onChange={handleGalleryImg} />

            {/* ── Primary image ── */}
            <Text style={ms.section}>Primary Image</Text>
            <View style={ms.imageRow}>
              {form.primaryImage
                ? <Image source={{ uri: form.primaryImage }} style={ms.primaryImg} resizeMode="cover" />
                : <View style={[ms.primaryImg, ms.imgPlaceholder]}><Text style={{ fontSize: 40 }}>🛍️</Text><Text style={ms.imgPlaceholderText}>No image</Text></View>
              }
              <View style={ms.imageBtns}>
                <TouchableOpacity style={ms.uploadBtn} onPress={() => primaryImgRef.current?.click()} disabled={imgUploading === 'primary'}>
                  {imgUploading === 'primary'
                    ? <ActivityIndicator color="#fff" size="small" />
                    : <Text style={ms.uploadBtnText}>{form.primaryImage ? 'Change' : 'Upload'}</Text>
                  }
                </TouchableOpacity>
                {form.primaryImage
                  ? <TouchableOpacity style={ms.removeBtn} onPress={() => setF('primaryImage', '')}>
                      <Text style={ms.removeBtnText}>Remove</Text>
                    </TouchableOpacity>
                  : null
                }
              </View>
            </View>

            {/* ── Basic info ── */}
            <Text style={ms.section}>Basic Info</Text>
            <FInput label="Product Name *" value={form.productName} onChange={v => setF('productName', v)} />
            <FInput label="Return Policy" value={form.returnPolicy} onChange={v => setF('returnPolicy', v)} />
            <FInput label="Warranty" value={form.warranty} onChange={v => setF('warranty', v)} />

            {/* ── Vendor / Branch ── */}
            <Text style={ms.section}>Vendor / Branch</Text>
            <FInput label="Branch ID" value={form.branch?.branchId} onChange={v => setF('branch', { ...form.branch, branchId: v })} />
            <FInput label="Branch Name" value={form.branch?.branchName} onChange={v => setF('branch', { ...form.branch, branchName: v })} />
            <FInput label="Branch Location" value={form.branch?.branchLocation} onChange={v => setF('branch', { ...form.branch, branchLocation: v })} />

            {/* ── Categories ── */}
            <Text style={ms.section}>Categories</Text>
            <FInput label="Category" value={form.category} onChange={v => setF('category', v)} />
            <FInput label="Subcategory" value={form.subcategory} onChange={v => setF('subcategory', v)} />
            <FInput label="Nested Subcategory" value={form.nestedsubcategory} onChange={v => setF('nestedsubcategory', v)} />

            {/* ── Toggles ── */}
            <Text style={ms.section}>Status</Text>
            {([
              { label: 'Approved', sub: 'Visible to customers', field: 'aproved' },
              { label: 'Discounted / On Sale', sub: 'Show sale badge', field: 'discounted' },
              { label: 'Sponsored', sub: 'Show sponsored badge', field: 'sponsored' },
              { label: 'In Stock', sub: 'Stock availability', field: 'stockAvailability' },
            ] as const).map(({ label, sub, field }) => (
              <View key={field} style={ms.switchRow}>
                <View>
                  <Text style={ms.switchLabel}>{label}</Text>
                  <Text style={ms.switchSub}>{sub}</Text>
                </View>
                <Switch
                  value={!!(form as any)[field]}
                  onValueChange={val => setF(field, val)}
                  trackColor={{ false: C.border, true: C.primary }}
                  thumbColor="#fff"
                />
              </View>
            ))}

            {/* ── Gallery ── */}
            <Text style={ms.section}>Gallery Images</Text>
            <TouchableOpacity style={ms.addGalleryBtn} onPress={() => galleryImgRef.current?.click()} disabled={imgUploading === 'gallery'}>
              {imgUploading === 'gallery'
                ? <ActivityIndicator color={C.primary} size="small" />
                : <Text style={ms.addGalleryText}>+ Add Images</Text>
              }
            </TouchableOpacity>
            {(form.images ?? []).length > 0 && (
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginTop: 10 }}>
                {(form.images ?? []).map((uri: string, i: number) => (
                  <View key={i} style={{ marginRight: 10 }}>
                    <Image source={{ uri }} style={ms.galleryThumb} resizeMode="cover" />
                    <TouchableOpacity style={ms.galleryDel} onPress={() => removeGalleryImg(uri)}>
                      <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>✕</Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </ScrollView>
            )}

            {/* ── Features ── */}
            <Text style={ms.section}>Features</Text>
            <TextInput
              style={[ms.input, { height: 80 }]}
              value={(form.features ?? []).join('\n')}
              onChangeText={v => setF('features', v.split('\n').map(x => x.trim()).filter(Boolean))}
              multiline
              placeholder="One feature per line"
              placeholderTextColor={C.textMuted}
            />

            {/* ── Search keywords ── */}
            <Text style={ms.section}>Search Keywords</Text>
            <TextInput
              style={ms.input}
              value={(form.searchKey ?? []).join(', ')}
              onChangeText={v => setF('searchKey', v.split(',').map(x => x.trim()).filter(Boolean))}
              placeholder="keyword1, keyword2, ..."
              placeholderTextColor={C.textMuted}
            />

            {/* ── Colours & Sizes ── */}
            <Text style={ms.section}>Colors & Sizes</Text>
            {/* Add colour */}
            <View style={ms.addColorRow}>
              <TextInput
                style={[ms.input, { flex: 1 }]}
                value={newColorName}
                onChangeText={setNewColorName}
                placeholder="Color name"
                placeholderTextColor={C.textMuted}
              />
              <input
                type="color"
                value={newColorHex}
                onChange={(e: any) => setNewColorHex(e.target.value)}
                style={{ width: 46, height: 46, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 10, marginLeft: 8 }}
              />
              <TouchableOpacity style={ms.addColorBtn} onPress={addColor}>
                <Text style={ms.addColorBtnText}>+ Add</Text>
              </TouchableOpacity>
            </View>

            {/* Colour list */}
            {(form.colors ?? []).map((color: any, ci: number) => (
              <ColorEditor
                key={ci}
                color={color}
                colorIdx={ci}
                onRemoveColor={() => removeColor(ci)}
                onAddSize={(sz) => addSize(ci, sz)}
                onRemoveSize={(si) => removeSize(ci, si)}
              />
            ))}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
}

// ── Inline form field ─────────────────────────────────────────────
function FInput({ label, value, onChange }: { label: string; value?: string; onChange: (v: string) => void }) {
  return (
    <View style={ms.formGroup}>
      <Text style={ms.label}>{label}</Text>
      <TextInput
        style={ms.input}
        value={String(value ?? '')}
        onChangeText={onChange}
        placeholder={label}
        placeholderTextColor="#94a3b8"
      />
    </View>
  );
}

// ── Color editor sub-component ────────────────────────────────────
function ColorEditor({ color, colorIdx, onRemoveColor, onAddSize, onRemoveSize }: {
  color: any; colorIdx: number;
  onRemoveColor: () => void;
  onAddSize: (s: ColorInSize) => void;
  onRemoveSize: (i: number) => void;
}) {
  const [sizeName, setSizeName] = useState('');
  const [price, setPrice] = useState('');
  const [mrp, setMrp] = useState('');
  const [stock, setStock] = useState('');

  const addSize = () => {
    if (!sizeName.trim()) return;
    onAddSize({
      sizeName: sizeName.trim(),
      price: parseFloat(price) || 0,
      mrp: parseFloat(mrp) || 0,
      stock: parseInt(stock) || 0,
      colorInSize: color.hex || '#000',
    });
    setSizeName(''); setPrice(''); setMrp(''); setStock('');
  };

  return (
    <View style={ce.wrap}>
      <View style={ce.header}>
        <View style={[ce.dot, { backgroundColor: color.hex || '#64748b' }]} />
        <Text style={ce.colorName}>{color.colorName}</Text>
        <TouchableOpacity onPress={onRemoveColor} style={ce.removeColorBtn}>
          <Text style={ce.removeColorText}>Remove Color</Text>
        </TouchableOpacity>
      </View>

      {/* Sizes */}
      {(color.sizes ?? []).map((sz: ColorInSize, i: number) => (
        <View key={i} style={ce.sizeRow}>
          <Text style={ce.sizeLabel}>{sz.sizeName}</Text>
          <Text style={ce.sizePrice}>₹{sz.price} <Text style={ce.sizeMrp}>₹{sz.mrp}</Text></Text>
          <Text style={ce.sizeStock}>Stock: {sz.stock}</Text>
          <TouchableOpacity onPress={() => onRemoveSize(i)}>
            <Text style={{ color: '#ef4444', fontWeight: '700', paddingHorizontal: 8 }}>✕</Text>
          </TouchableOpacity>
        </View>
      ))}

      {/* Add size row */}
      <View style={ce.addSizeRow}>
        <TextInput style={[ms.input, ce.sizeInput]} value={sizeName} onChangeText={setSizeName} placeholder="Size name" placeholderTextColor="#94a3b8" />
        <TextInput style={[ms.input, ce.sizeInput]} value={price} onChangeText={setPrice} placeholder="Price" keyboardType="numeric" placeholderTextColor="#94a3b8" />
        <TextInput style={[ms.input, ce.sizeInput]} value={mrp} onChangeText={setMrp} placeholder="MRP" keyboardType="numeric" placeholderTextColor="#94a3b8" />
        <TextInput style={[ms.input, ce.sizeInput]} value={stock} onChangeText={setStock} placeholder="Stock" keyboardType="numeric" placeholderTextColor="#94a3b8" />
        <TouchableOpacity style={ce.addSizeBtn} onPress={addSize}>
          <Text style={ce.addSizeText}>+ Size</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// ── Styles ────────────────────────────────────────────────────────
const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f6f8fb', padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  headerMobile: { flexDirection: 'column', alignItems: 'flex-start', gap: 10 },
  title: { fontSize: 24, fontWeight: '700', color: '#1a2332' },
  subtitle: { fontSize: 13, color: '#64748b', marginTop: 2 },
  addBtn: { backgroundColor: '#21808D', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10 },
  addBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },

  filtersRow: { flexDirection: 'row', gap: 12, alignItems: 'center', marginBottom: 16 },
  searchBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: '#e2e8f0', paddingHorizontal: 14 },
  searchIcon: { fontSize: 14, marginRight: 8 },
  searchInput: { flex: 1, height: 46, fontSize: 14, color: '#1a2332' },

  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  loaderText: { marginTop: 12, color: '#64748b' },
  empty: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 80 },
  emptyIcon: { fontSize: 56, marginBottom: 16 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#1a2332', marginBottom: 6 },
  emptySubtitle: { fontSize: 14, color: '#64748b' },
});

const card = StyleSheet.create({
  wrap: {
    flex: 1, backgroundColor: '#fff', borderRadius: 18,
    shadowColor: '#000', shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08, shadowRadius: 8, elevation: 4, overflow: 'hidden',
  },
  imgWrap: { position: 'relative' },
  img: { width: '100%', height: 150, backgroundColor: '#f1f5f9' },
  imgPlaceholder: { justifyContent: 'center', alignItems: 'center' },
  badges: { position: 'absolute', top: 10, left: 10, gap: 4 },
  badge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, marginBottom: 4 },
  badgeText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  body: { padding: 14 },
  name: { fontSize: 15, fontWeight: '700', color: '#1a2332', marginBottom: 6, lineHeight: 22 },
  vendorRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6 },
  vendorIcon: { fontSize: 11 },
  vendorText: { fontSize: 11, color: '#21808D', fontWeight: '600', flex: 1 },
  chips: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
  chip: { backgroundColor: '#e8f5f7', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3 },
  chipText: { fontSize: 10, color: '#21808D', fontWeight: '600' },

  statsRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  stat: { flex: 1, backgroundColor: '#f6f8fb', borderRadius: 10, padding: 8, borderWidth: 1, borderColor: '#e2e8f0', alignItems: 'center' },
  statLabel: { fontSize: 10, color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase' },
  statValue: { fontSize: 15, fontWeight: '700', color: '#1a2332', marginTop: 2 },

  toggleRow: { flexDirection: 'row', gap: 8, marginBottom: 4 },
  toggleBtn: { flex: 1, backgroundColor: '#f6f8fb', borderRadius: 8, paddingVertical: 7, alignItems: 'center', borderWidth: 1, borderColor: '#e2e8f0' },
  toggleText: { fontSize: 11, fontWeight: '700' },

  gallery: { paddingHorizontal: 14, paddingBottom: 10 },
  galleryImg: { width: 72, height: 72, borderRadius: 8, marginRight: 8, backgroundColor: '#f1f5f9' },

  actions: { flexDirection: 'row', gap: 8, padding: 14, paddingTop: 0 },
  editBtn: { flex: 1, backgroundColor: '#e8f5f7', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  editText: { color: '#21808D', fontWeight: '700', fontSize: 13 },
  delBtn: { flex: 1, backgroundColor: '#fee2e2', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  delText: { color: '#ef4444', fontWeight: '700', fontSize: 13 },
});

const ms = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#fff' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  backBtn: { padding: 4 },
  backText: { color: '#64748b', fontSize: 15, fontWeight: '500' },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#1a2332' },
  saveBtn: { backgroundColor: '#21808D', paddingHorizontal: 18, paddingVertical: 8, borderRadius: 8 },
  saveBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  scroll: { padding: 20, paddingBottom: 40 },
  section: { fontSize: 13, fontWeight: '700', color: '#21808D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, marginTop: 16 },
  formGroup: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#374151', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#1a2332', backgroundColor: '#fafafa', marginBottom: 10 },

  imageRow: { flexDirection: 'row', gap: 16, alignItems: 'center', marginBottom: 8 },
  primaryImg: { width: 120, height: 120, borderRadius: 12, backgroundColor: '#f1f5f9' },
  imgPlaceholder: { justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#e2e8f0' },
  imgPlaceholderText: { color: '#94a3b8', fontSize: 11, marginTop: 6 },
  imageBtns: { gap: 10 },
  uploadBtn: { backgroundColor: '#21808D', paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10, minWidth: 100, alignItems: 'center' },
  uploadBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  removeBtn: { backgroundColor: '#fee2e2', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10, alignItems: 'center' },
  removeBtnText: { color: '#ef4444', fontWeight: '600', fontSize: 12 },

  addGalleryBtn: { borderWidth: 2, borderColor: '#21808D', borderRadius: 10, paddingVertical: 12, alignItems: 'center', borderStyle: 'dashed' },
  addGalleryText: { color: '#21808D', fontWeight: '600', fontSize: 14 },
  galleryThumb: { width: 80, height: 80, borderRadius: 8, backgroundColor: '#f1f5f9' },
  galleryDel: { position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(0,0,0,0.6)', width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },

  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  switchLabel: { fontSize: 14, fontWeight: '600', color: '#1a2332' },
  switchSub: { fontSize: 12, color: '#64748b', marginTop: 2 },

  addColorRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  addColorBtn: { backgroundColor: '#21808D', paddingHorizontal: 14, paddingVertical: 12, borderRadius: 10, marginLeft: 8 },
  addColorBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

const ce = StyleSheet.create({
  wrap: { backgroundColor: '#f6f8fb', borderRadius: 12, padding: 14, marginBottom: 14, borderWidth: 1, borderColor: '#e2e8f0' },
  header: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  dot: { width: 18, height: 18, borderRadius: 9, marginRight: 8 },
  colorName: { flex: 1, fontSize: 14, fontWeight: '700', color: '#1a2332' },
  removeColorBtn: { backgroundColor: '#fee2e2', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 },
  removeColorText: { color: '#ef4444', fontSize: 11, fontWeight: '600' },
  sizeRow: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 8, padding: 10, marginBottom: 6, borderWidth: 1, borderColor: '#e2e8f0', gap: 8 },
  sizeLabel: { flex: 1, fontSize: 13, fontWeight: '700', color: '#1a2332' },
  sizePrice: { fontSize: 13, fontWeight: '600', color: '#1a2332' },
  sizeMrp: { fontSize: 11, color: '#94a3b8', textDecorationLine: 'line-through' },
  sizeStock: { fontSize: 11, color: '#64748b' },
  addSizeRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 8 },
  sizeInput: { flex: 1, minWidth: 80, marginBottom: 0 },
  addSizeBtn: { backgroundColor: '#21808D', paddingHorizontal: 12, paddingVertical: 12, borderRadius: 10 },
  addSizeText: { color: '#fff', fontWeight: '600', fontSize: 12 },
});
